<template>
  <div class="tw-border tw-rounded-lg tw-p-4 tw-bg-white">
    <!-- Group Header with Editable Name -->
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
      <div class="tw-flex tw-items-center tw-gap-2">
        <template v-if="isEditingName">
          <input
            ref="nameInputRef"
            v-model="localName"
            @blur="saveName"
            @keyup.enter="saveName"
            @keyup.escape="cancelEditName"
            class="tw-w-32 tw-px-2 tw-py-1 tw-text-sm tw-font-medium tw-border tw-border-secondary-300 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500"
          />
        </template>
        <template v-else>
          <h5 class="tw-font-medium tw-text-sm tw-m-0">
            {{ group.name }} ({{ group.assignees.length }})
          </h5>
          <button
            v-if="!readonly"
            type="button"
            @click="startEditName"
            class="tw-text-gray-400 hover:tw-text-gray-600 tw-p-1"
          >
            <Pencil class="tw-w-3 tw-h-3" />
          </button>
        </template>
      </div>
      <UserDropdownSelect
        v-if="!readonly"
        :users="availableUsers"
        :placeholder="t('customer_management.assign_team.add_user')"
        trigger-class="tw-w-32 tw-h-8 tw-px-2 tw-text-xs tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
        @select="handleAddUser"
      />
    </div>

    <!-- Assigned Users -->
    <div class="tw-min-h-[50px] tw-bg-gray-50 tw-rounded-lg tw-p-3">
      <div v-if="group.assignees.length > 0" class="tw-flex tw-flex-wrap tw-gap-2">
        <Badge
          v-for="userId in group.assignees"
          :key="userId"
          variant="secondary"
          class="tw-gap-1 tw-bg-white tw-border tw-text-xs"
        >
          {{ getUserName(userId) }}
          <button
            v-if="!readonly"
            type="button"
            @click="handleRemoveUser(userId)"
            class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5"
          >
            <X class="tw-w-3 tw-h-3" />
          </button>
        </Badge>
      </div>
      <p v-else class="tw-text-xs tw-text-gray-400 tw-m-0">
        {{ t('customer_management.assign_team.no_users_assigned') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Pencil, X } from 'lucide-vue-next';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import UserDropdownSelect from './UserDropdownSelect.vue';

const { t } = useI18n();

interface User {
  id: number | string;
  display_name: string;
  email: string;
}

interface ClearingGroup {
  id?: number;
  name: string;
  display_order: number;
  assignees: (number | string)[];
}

interface Props {
  group: ClearingGroup;
  allUsers: User[];
  usersInOtherGroups: (number | string)[];
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
});

const emit = defineEmits<{
  'update:name': [name: string];
  'add-user': [userId: number | string];
  'remove-user': [userId: number | string];
}>();

// Name editing state
const isEditingName = ref(false);
const localName = ref(props.group.name);
const nameInputRef = ref<HTMLInputElement | null>(null);

// Computed: Filter out users already in this group or other groups
const availableUsers = computed(() => {
  return props.allUsers.filter(
    user => !props.group.assignees.includes(user.id) && !props.usersInOtherGroups.includes(user.id)
  );
});

// Methods
const getUserName = (userId: number | string) => {
  const user = props.allUsers.find(u => u.id === userId);
  return user ? user.display_name : t('customer_management.assign_team.unknown_user');
};

const startEditName = () => {
  localName.value = props.group.name;
  isEditingName.value = true;
  nextTick(() => {
    nameInputRef.value?.focus();
    nameInputRef.value?.select();
  });
};

const saveName = () => {
  if (localName.value.trim() && localName.value !== props.group.name) {
    emit('update:name', localName.value.trim());
  }
  isEditingName.value = false;
};

const cancelEditName = () => {
  localName.value = props.group.name;
  isEditingName.value = false;
};

const handleAddUser = (userId: number | string) => {
  emit('add-user', userId);
};

const handleRemoveUser = (userId: number | string) => {
  emit('remove-user', userId);
};
</script>
